/**
 * Per-user view-tab order (`specs/M4-V1-views.md` §2.2 / §4).
 *
 * monday's tab menu says **"Reorder (for you only)"**, so moving a tab changes the order for the
 * mover alone. `views.position` stays the account-wide default — the importer writes it and `/v2`
 * returns it — and a user's overrides live in `view_user_state.state.tabPosition`.
 */
/**
 * The order a given user sees: their override where they have one, the shared position where
 * they do not, and pinned views ahead of everything (§4, q294 — pinning is board-wide).
 */
export function personalViewOrder(views, overrides = {}) {
    return [...views].sort((a, b) => {
        const pinnedA = a.pinned === true ? 0 : 1;
        const pinnedB = b.pinned === true ? 0 : 1;
        if (pinnedA !== pinnedB)
            return pinnedA - pinnedB;
        const keyA = (a.id !== null ? overrides[a.id] : undefined) ?? a.position;
        const keyB = (b.id !== null ? overrides[b.id] : undefined) ?? b.position;
        if (keyA === keyB)
            return a.position < b.position ? -1 : a.position > b.position ? 1 : 0;
        return keyA < keyB ? -1 : 1;
    });
}
/**
 * Where a view lands after one *Move back / ahead / to first / to last*. Returns the new override
 * map, so the caller writes one `view_user_state` row per changed view and nothing else.
 *
 * The whole visible order is renumbered rather than a gap being found: a tab bar holds tens of
 * views, not thousands, and a renumber can never collide.
 */
export function applyPersonalReorder(views, overrides, viewId, method) {
    const ordered = personalViewOrder(views, overrides).filter((view) => view.id !== null);
    const index = ordered.findIndex((view) => view.id === viewId);
    if (index === -1)
        return { ...overrides };
    const target = method === 'first' ? 0
        : method === 'last' ? ordered.length - 1
            : method === 'back' ? Math.max(0, index - 1)
                : Math.min(ordered.length - 1, index + 1);
    if (target === index)
        return { ...overrides };
    const moved = ordered.splice(index, 1)[0];
    ordered.splice(target, 0, moved);
    const next = {};
    ordered.forEach((view, position) => {
        next[view.id] = String(position + 1).padStart(6, '0');
    });
    return next;
}
//# sourceMappingURL=personal-order.js.map